import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  List,
  ListItem,
} from "@mui/material";

const CountryReportsComponent = ({
  stepId, // Add stepId prop to identify each step
  onPrevious,
  onNext,
  initialItems = [],
  title = "Laws and Regulations",
  legislationButtonTitle = "Federal Environmental Laws",
  regulationButtonTitle = "Federal Environmental Regulations",
  buttonStyles = {},
  downloadButtonShow = false,
  // New props for external state management
  externalSelectedItems = null,
  onSelectedItemsChange = null,
  externalActiveProvince = null,
  onActiveProvinceChange = null,
  externalActiveTab = null,
  onActiveTabChange = null,
}) => {
  const isProvinceData = initialItems.length > 0 && initialItems[0].name && 
    (initialItems[0].legislation || initialItems[0].regulations);
  
  const initializeSelectedItems = () => {
    if (isProvinceData) {
      const initial = {};
      initialItems.forEach((province) => {
        initial[province.name] = { legislation: [], regulations: [] };
      });
      return initial;
    } else {
      return {
        legislation: [],
        regulation: [],
      };
    }
  };

  // Use external state if provided, otherwise use internal state
  const [internalSelectedItems, setInternalSelectedItems] = useState(() => initializeSelectedItems());
  const [internalActiveProvince, setInternalActiveProvince] = useState(null);
  const [internalActiveTab, setInternalActiveTab] = useState(null);

  const selectedItems = externalSelectedItems !== null ? externalSelectedItems : internalSelectedItems;
  const activeProvince = externalActiveProvince !== null ? externalActiveProvince : internalActiveProvince;
  const activeTab = externalActiveTab !== null ? externalActiveTab : internalActiveTab;

  const setSelectedItems = (newItems) => {
    if (onSelectedItemsChange) {
      onSelectedItemsChange(stepId, typeof newItems === 'function' ? newItems(selectedItems) : newItems);
    } else {
      setInternalSelectedItems(newItems);
    }
  };

  const setActiveProvince = (province) => {
    if (onActiveProvinceChange) {
      onActiveProvinceChange(stepId, province);
    } else {
      setInternalActiveProvince(province);
    }
  };

  const setActiveTab = (tab) => {
    if (onActiveTabChange) {
      onActiveTabChange(stepId, tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  useEffect(() => {
    if (externalSelectedItems === null) {
      setInternalSelectedItems(initializeSelectedItems());
    }
  }, [initialItems]);

  const processedData = !isProvinceData && initialItems.length > 0
    ? initialItems[0]
    : { legislation: [], regulations: [] };

  const handleProvinceSelect = (provinceName) => {
    if (activeProvince === provinceName) {
      setActiveProvince(null);
      setActiveTab(null);
    } else {
      setActiveProvince(provinceName);
      setActiveTab(null); 
    }
  };

  const handleItemToggle = (provinceName, type, itemId) => {
    if (isProvinceData) {
      setSelectedItems((prev) => ({
        ...prev,
        [provinceName]: {
          ...(prev[provinceName] || { legislation: [], regulations: [] }),
          [type]: (prev[provinceName]?.[type] || []).includes(itemId)
            ? (prev[provinceName]?.[type] || []).filter((id) => id !== itemId)
            : [...(prev[provinceName]?.[type] || []), itemId],
        },
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [type]: (prev[type] || []).includes(itemId)
          ? (prev[type] || []).filter((id) => id !== itemId)
          : [...(prev[type] || []), itemId],
      }));
    }
  };

  const handleSelectAll = (provinceName, type) => {
    if (isProvinceData) {
      const provinceData = initialItems.find((item) => item.name === provinceName);
      const allItems = type === "legislation"
        ? provinceData.legislation.map((_, index) => index)
        : provinceData.regulations.map((_, index) => index);

      setSelectedItems((prev) => ({
        ...prev,
        [provinceName]: {
          ...(prev[provinceName] || { legislation: [], regulations: [] }),
          [type]: (prev[provinceName]?.[type] || []).length === allItems.length ? [] : allItems,
        },
      }));
    } else {
      const allItems = type === "legislation"
        ? processedData.legislation.map((_, index) => index)
        : processedData.regulations.map((_, index) => index);

      setSelectedItems((prev) => ({
        ...prev,
        [type]: (prev[type] || []).length === allItems.length ? [] : allItems,
      }));
    }
  };

  const getCurrentItems = () => {
    if (isProvinceData) {
      if (!activeProvince || !activeTab) return [];
      const provinceData = initialItems.find((item) => item.name === activeProvince);
      return activeTab === "legislation" ? provinceData.legislation : provinceData.regulations;
    } else {
      return activeTab === "legislation"
        ? processedData.legislation
        : processedData.regulations;
    }
  };

  const getCurrentSelected = () => {
    if (isProvinceData) {
      if (!activeProvince || !activeTab) return [];
      return selectedItems[activeProvince]?.[activeTab] || [];
    } else {
      return selectedItems[activeTab] || [];
    }
  };

  const handleDownload = () => {
    if (isProvinceData) {
      const downloadData = initialItems.map((province) => ({
        name: province.name,
        legislation: (selectedItems[province.name]?.legislation || [])
          .map((index) => province.legislation[index])
          .filter(Boolean),
        regulations: (selectedItems[province.name]?.regulations || [])
          .map((index) => province.regulations[index])
          .filter(Boolean),
      }));
      alert(`Download initiated!\n${JSON.stringify(downloadData, null, 2)}`);
    } else {
      const selectedLegislation = (selectedItems.legislation || [])
        .map((index) => processedData.legislation[index])
        .filter(Boolean);
      const selectedRegulations = (selectedItems.regulation || [])
        .map((index) => processedData.regulations[index])
        .filter(Boolean);
      alert(
        `Download initiated!\nLegislation: ${selectedLegislation.length} items\nRegulations: ${selectedRegulations.length} items`
      );
    }
  };

  const totalSelected = isProvinceData
    ? initialItems.reduce(
        (sum, province) =>
          sum +
          (selectedItems[province.name]?.legislation?.length || 0) +
          (selectedItems[province.name]?.regulations?.length || 0),
        0
      )
    : (selectedItems.legislation?.length || 0) + (selectedItems.regulation?.length || 0);

  const currentItems = getCurrentItems();
  const currentSelected = getCurrentSelected();

  const defaultButtonStyles = {
    download: {
      background: totalSelected === 0
        ? '#D3D3D3'
        : '#02462E',
      width: '252px',
      height: '50px',
      color: '#fff',
      border: 'none',
      textTransform: 'none',
      fontFamily: 'DM Sans',
      fontWeight: 600,
      fontSize: '16px',
      cursor: totalSelected === 0 ? 'not-allowed' : 'pointer',
      borderRadius: '10px',
      '&:hover': {
        background: totalSelected === 0
          ? '#D3D3D3'
          : '#02462E',
      },
    },
    next: {
      background: '#02462E',
      width: '252px',
      height: '50px',
      borderRadius: '10px',
      color: '#fff',
      textTransform: 'none',
      fontFamily: 'DM Sans',
      fontWeight: 600,
      fontSize: '16px',
      '&:hover': {
        background: '#02462E',
      },
    },
    tab: {
      background: '#E0E0E0',
      color: '#333',
      borderRadius: '10px',
      fontWeight: 600,
      textTransform: 'none',
      fontFamily: 'DM Sans',
      fontSize: '15px',
      height: '44px',
      minWidth: '220px',
      '&:hover': {
        background: '#D3D3D3',
      },
    },
    activeTab: {
      background: 'linear-gradient(180deg, #02462E 0%, #02462E 100%)',
      color: '#fff',
      borderRadius: '10px',
      fontWeight: 600,
      textTransform: 'none',
      fontFamily: 'DM Sans',
      fontSize: '15px',
      height: '44px',
      minWidth: '220px',
      '&:hover': {
        background: 'linear-gradient(180deg, #02462E 0%, #02462E 100%)',
      },
    },
  };

  const defaultTitleStyles = {
    fontFamily: 'DM Sans',
    fontWeight: 800,
    fontSize: '24px',
    lineHeight: '42px',
    color: '#02462E',
    mb: 2,
  };

  const mergedButtonStyles = {
    download: { ...defaultButtonStyles.download, ...buttonStyles.download },
    next: { ...defaultButtonStyles.next, ...buttonStyles.next },
    tab: { ...defaultButtonStyles.tab, ...buttonStyles.tab },
    activeTab: { ...defaultButtonStyles.activeTab, ...buttonStyles.activeTab },
  };

  const mergedTitleStyles = { ...defaultTitleStyles };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ p: 3, maxWidth: "100%" }}>
          <Typography variant="h6" sx={mergedTitleStyles}>
            {title}
          </Typography>

          {isProvinceData && (
            <>
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                {initialItems.map((province) => (
                  <Button
                    key={province.name}
                    variant="contained"
                    onClick={() => handleProvinceSelect(province.name)}
                    sx={activeProvince === province.name ? mergedButtonStyles.activeTab : mergedButtonStyles.tab}
                  >
                    {province.name}
                  </Button>
                ))}
              </Box>
              
              {/* Separator line between province buttons and legislation/regulation buttons */}
              <Box 
                sx={{ 
                  width: '100%', 
                  height: '1px', 
                  backgroundColor: '#E0E0E0', 
                  mb: 2,
                  mx: 0
                }} 
              />
            </>
          )}

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            {isProvinceData ? (
              activeProvince && (
                <>
                  <Button
                    variant="contained"
                    onClick={() => setActiveTab(activeTab === "legislation" ? null : "legislation")}
                    sx={activeTab === "legislation" ? mergedButtonStyles.activeTab : mergedButtonStyles.tab}
                  >
                    {`${activeProvince} Legislation`}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setActiveTab(activeTab === "regulations" ? null : "regulations")}
                    sx={activeTab === "regulations" ? mergedButtonStyles.activeTab : mergedButtonStyles.tab}
                  >
                    {`${activeProvince} Regulations`}
                  </Button>
                </>
              )
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(activeTab === "legislation" ? null : "legislation")}
                  sx={activeTab === "legislation" ? mergedButtonStyles.activeTab : mergedButtonStyles.tab}
                >
                  {legislationButtonTitle}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(activeTab === "regulation" ? null : "regulation")}
                  sx={activeTab === "regulation" ? mergedButtonStyles.activeTab : mergedButtonStyles.tab}
                >
                  {regulationButtonTitle}
                </Button>
              </>
            )}
          </Box>

          {((isProvinceData && activeProvince && activeTab) || (!isProvinceData && activeTab)) && (
            <List disablePadding>
              <ListItem disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        currentSelected.length === currentItems.length &&
                        currentItems.length > 0
                      }
                      onChange={() => isProvinceData 
                        ? handleSelectAll(activeProvince, activeTab)
                        : handleSelectAll(null, activeTab)
                      }
                      sx={{
                        color: '#02462E',
                        '&.Mui-checked': { color: '#02462E' },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: 'DM Sans',
                        fontWeight: 700,
                        fontSize: '16px',
                        lineHeight: '28px',
                        color: '#02462E',
                      }}
                    >
                      Select All
                    </Typography>
                  }
                />
              </ListItem>

              {currentItems.length > 0 ? (
                currentItems.flatMap((item, index) => {
                  const isChecked = currentSelected.includes(index);
                  const itemsToRender = [];
                  
                  if (typeof item === "string") {
                    itemsToRender.push(
                      <ListItem key={index} disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => isProvinceData
                                ? handleItemToggle(activeProvince, activeTab, index)
                                : handleItemToggle(null, activeTab, index)
                              }
                              sx={{
                                color: '#02462E',
                                '&.Mui-checked': { color: '#02462E' },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontFamily: 'DM Sans',
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: '28px',
                                letterSpacing: '-2%',
                                color: isChecked ? '#02462E' : '#A3AED0',
                              }}
                            >
                              {item}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  } else {
                    itemsToRender.push(
                      <ListItem key={index} disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => isProvinceData
                                ? handleItemToggle(activeProvince, activeTab, index)
                                : handleItemToggle(null, activeTab, index)
                              }
                              sx={{
                                color: '#02462E',
                                '&.Mui-checked': { color: '#02462E' },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontFamily: 'DM Sans',
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: '28px',
                                letterSpacing: '-2%',
                                color: isChecked ? '#02462E' : '#A3AED0',
                              }}
                            >
                              {item.name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                    
                    if (!isProvinceData && item.subRegulations) {
                      item.subRegulations.forEach((subItem, subIndex) => {
                        const subItemIndex = `${index}-${subIndex}`;
                        const isSubChecked = currentSelected.includes(subItemIndex);
                        itemsToRender.push(
                          <ListItem
                            key={subItemIndex}
                            disableGutters
                            sx={{ py: 0.5, alignItems: 'flex-start', pl: 4 }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSubChecked}
                                  onChange={() => handleItemToggle(null, activeTab, subItemIndex)}
                                  sx={{
                                    color: '#02462E',
                                    '&.Mui-checked': { color: '#02462E' },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontFamily: 'DM Sans',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '24px',
                                    color: isSubChecked ? '#02462E' : '#A3AED0',
                                  }}
                                >
                                  {subItem}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                      });
                    }
                  }
                  return itemsToRender;
                })
              ) : (
                <ListItem disableGutters sx={{ py: 4, justifyContent: 'center' }}>
                  <Typography
                    sx={{
                      fontFamily: 'DM Sans',
                      fontWeight: 400,
                      fontSize: '15px',
                      lineHeight: '24px',
                      color: '#555E75',
                      fontStyle: 'italic',
                    }}
                  >
                    No {activeTab === "legislation" ? "legislation" : "regulations"} available
                  </Typography>
                </ListItem>
              )}
            </List>
          )}

          {isProvinceData && !activeProvince && (
            <Typography
              sx={{
                fontFamily: 'DM Sans',
                fontWeight: 400,
                fontSize: '15px',
                lineHeight: '24px',
                color: '#555E75',
                fontStyle: 'italic',
                textAlign: 'center',
                mb: 2,
              }}
            >
              Please select a province above to view items.
            </Typography>
          )}

          {isProvinceData && activeProvince && !activeTab && (
            <Typography
              sx={{
                fontFamily: 'DM Sans',
                fontWeight: 400,
                fontSize: '15px',
                lineHeight: '24px',
                color: '#555E75',
                fontStyle: 'italic',
                textAlign: 'center',
                mb: 2,
              }}
            >
              Please select legislation or regulations to view items.
            </Typography>
          )}

          {!isProvinceData && !activeTab && (
            <Typography
              sx={{
                fontFamily: 'DM Sans',
                fontWeight: 400,
                fontSize: '15px',
                lineHeight: '24px',
                color: '#555E75',
                fontStyle: 'italic',
                textAlign: 'center',
                mb: 2,
              }}
            >
              Please select a category above to view items.
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            {downloadButtonShow && (
              <Button
                variant="outlined"
                onClick={handleDownload}
                disabled={totalSelected === 0}
                sx={mergedButtonStyles.download}
              >
                Download Report
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onNext}
              sx={mergedButtonStyles.next}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CountryReportsComponent;